"use client"

import Link from "next/link"
import { Star, Heart, ShoppingCart } from "lucide-react"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { ImageWithFallback } from "@/components/ui/image-with-fallback"
import { formatCurrency } from "@/lib/utils"
import { useWishlist } from "@/contexts/wishlist-context"
import { useCart } from "@/contexts/cart-context"
import { useToast } from "@/hooks/use-toast"

interface Product {
  id: string
  slug: string
  name: string
  price: number
  comparePrice?: number
  originalPrice?: number
  image?: string
  images?: string[] | string
  rating?: number
  reviews?: number
  badge?: string
}

interface ProductCardProps {
  product: Product
  viewMode?: "grid" | "list"
}

export function ProductCard({ product, viewMode = "grid" }: ProductCardProps) {
  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist()
  const { addToCart } = useCart()
  const { toast } = useToast()
  
  // Ensure price is always a number
  const price = typeof product.price === 'number' ? product.price : 0
  const comparePrice = typeof product.comparePrice === 'number' ? product.comparePrice : undefined
  
  const discount = (comparePrice || product.originalPrice)
    ? Math.round(((comparePrice || product.originalPrice!) - price) / (comparePrice || product.originalPrice!) * 100)
    : 0

  const handleWishlistClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (isInWishlist(product.id)) {
      removeFromWishlist(product.id)
    } else {
      addToWishlist(product.id)
    }
  }

  const handleAddToCart = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    
    try {
      await addToCart({
        productId: product.id,
        quantity: 1,
        price: price,
      })
      
      toast({
        title: "Added to cart!",
        description: `${product.name} has been added to your cart.`,
      })
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add item to cart. Please try again.",
        variant: "destructive",
      })
    }
  }

  // Get the first image from the images array or use the image prop
  const getProductImage = () => {
    // If there's a direct image prop, use it
    if (product.image) {
      return product.image
    }
    
    // If images is an array and has items, use the first one
    if (Array.isArray(product.images) && product.images.length > 0) {
      return product.images[0]
    }
    
    // If images is a string (JSON), try to parse it
    if (typeof product.images === 'string' && product.images) {
      try {
        const parsedImages = JSON.parse(product.images)
        if (Array.isArray(parsedImages) && parsedImages.length > 0) {
          return parsedImages[0]
        }
      } catch (error) {
        console.error('Error parsing product images:', error)
      }
    }
    
    // Fallback to placeholder
    return "/placeholder.svg"
  }

  const productImage = getProductImage()

  // Default values for rating and reviews
  const rating = product.rating || 0
  const reviews = product.reviews || 0

  return (
    <Card className={`group hover:shadow-lg transition-all duration-slow hover:-translate-y-1 ${
      viewMode === "list" ? "flex flex-row" : "flex flex-col h-full"
    }`}>
      <CardContent className={`p-0 flex ${viewMode === "list" ? "flex-row w-full" : "flex-col flex-1"}`}>
        {/* Image Section */}
        <div className={`relative overflow-hidden ${
          viewMode === "list" ? "w-32 h-32 rounded-l-lg" : "rounded-t-lg aspect-square"
        }`}>
          <Link href={`/product/${product.slug}`} className="relative block w-full h-full">
            <ImageWithFallback
              src={productImage}
              alt={product.name}
              fill
              fallbackSrc="/placeholder.svg"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-slow"
            />
          </Link>
          
          {/* Badges */}
          {product.badge && (
            <Badge className="absolute top-2 left-2 text-xs">
              {product.badge}
            </Badge>
          )}
          {discount > 0 && (
            <Badge variant="destructive" className="absolute top-2 right-2 text-xs">
              -{discount}%
            </Badge>
          )}
          
          {/* Wishlist Button */}
          <Button
            size="icon"
            variant="secondary"
            className={`absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity ${
              isInWishlist(product.id) ? "text-red-500" : ""
            }`}
            style={{ display: discount > 0 ? "none" : "flex" }}
            onClick={handleWishlistClick}
          >
            <Heart className={`h-4 w-4 ${isInWishlist(product.id) ? "fill-current" : ""}`} />
          </Button>
        </div>

        {/* Content Section */}
        <div className={`flex flex-col flex-1 ${
          viewMode === "list" ? "p-4 justify-between" : "p-3 sm:p-4"
        }`}>
          {/* Product Name */}
          <Link href={`/product/${product.slug}`} className="flex-1">
            <h3 className={`font-medium hover:text-primary-blue transition-colors leading-tight ${
              viewMode === "list" ? "text-base mb-2" : "text-sm sm:text-base mb-2 line-clamp-2 text-pretty"
            }`}>
              {product.name}
            </h3>
          </Link>

          {/* Rating */}
          <div className="flex items-center gap-1 mb-2 flex-shrink-0">
            <div className="flex items-center">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className={`h-3 w-3 ${
                    i < Math.floor(rating) ? "fill-yellow-400 text-yellow-400" : "text-gray-300"
                  }`}
                />
              ))}
            </div>
            <span className="text-xs text-muted-foreground">({reviews})</span>
          </div>

          {/* Price and Add to Cart Section */}
          <div className={`${viewMode === "list" ? "mt-2" : "mt-auto pt-2 border-t"}`}>
            <div className={`flex items-center gap-2 ${
              viewMode === "list" ? "justify-between" : ""
            }`}>
              {/* Price Section */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 min-w-0 flex-1">
                <div className="font-semibold text-green-600 text-sm sm:text-base">
                  {formatCurrency(price, '₹', { decimals: 0 })}
                </div>
                {(comparePrice || product.originalPrice) && (
                  <div className="text-xs line-through text-muted-foreground">
                    {formatCurrency(comparePrice || product.originalPrice!, '₹', { decimals: 0 })}
                  </div>
                )}
              </div>

              {/* Add to Cart Button */}
              <Button 
                size="sm" 
                className="gap-1 sm:gap-2 bg-blue-600 hover:bg-blue-700 flex-shrink-0 text-xs sm:text-sm px-2 sm:px-3"
                onClick={handleAddToCart}
              >
                <ShoppingCart className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">Add</span>
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
